/**
 * Piccolo helper per costruire DOM in modo dichiarativo.
 * Evita template-string innerHTML (più sicuro + più veloce).
 */
export type Child = Node | string | null | undefined | false;

export interface ElOpts {
  className?: string;
  text?: string;
  html?: string;
  attrs?: Record<string, string>;
  dataset?: Record<string, string>;
  aria?: Record<string, string>;
  role?: string;
  onClick?: (e: MouseEvent) => void;
  onChange?: (e: Event) => void;
  onInput?: (e: Event) => void;
  onKeyDown?: (e: KeyboardEvent) => void;
  onSubmit?: (e: SubmitEvent) => void;
  children?: Child[] | Child;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  opts: ElOpts = {},
  children?: Child[] | Child
): HTMLElementTagNameMap[K] {

  const node = document.createElement(tag);
  if (opts.className) node.className = opts.className;
  if (opts.text !== undefined) node.textContent = opts.text;
  if (opts.html !== undefined) node.innerHTML = opts.html;
  if (opts.role) node.setAttribute('role', opts.role);
  if (opts.attrs) {
    for (const [k, v] of Object.entries(opts.attrs)) node.setAttribute(k, v);
  }
  if (opts.dataset) {
    for (const [k, v] of Object.entries(opts.dataset)) node.dataset[k] = v;
  }
  if (opts.aria) {
    for (const [k, v] of Object.entries(opts.aria)) node.setAttribute(`aria-${k}`, v);
  }
  if (opts.onClick) node.addEventListener('click', opts.onClick as EventListener);
  if (opts.onChange) node.addEventListener('change', opts.onChange as EventListener);
  if (opts.onInput) node.addEventListener('input', opts.onInput as EventListener);
  if (opts.onKeyDown) node.addEventListener('keydown', opts.onKeyDown as EventListener);
  if (opts.onSubmit) node.addEventListener('submit', opts.onSubmit as EventListener);
  const kids = children ?? opts.children;
  if (kids) {
    const arr = Array.isArray(kids) ? kids : [kids];
    for (const c of arr) {
      if (c == null || c === false) continue;
      node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    }
  }
  return node;
}

/** Icona SVG inline (set minimale, stroke 2px). */
export function icon(name: 'calculator' | 'book' | 'settings' | 'info' | 'reset' | 'download' | 'upload' | 'chart' | 'check' | 'arrow', opt?: Omit<ElOpts, 'className'>): HTMLElement {
  const dict: Record<string, string> = {
    calculator: 'fas fa-calculator',
    book: 'fas fa-book',
    settings: 'fas fa-gear',
    info: 'fas fa-circle-info',
    reset: 'fas fa-arrow-rotate-left',
    download: 'fas fa-download',
    upload: 'fas fa-upload',
    chart: 'fas fa-chart-simple',
    check: 'fas fa-check',
    arrow: 'fas fa-arrow-right-long'
  };
  if (!dict[name]) {
    console.warn(`Icona non trovata: ${name}`);
  }

  return el('i', { className: `${dict[name]}`, ...opt, });
}

/** Tooltip accessibile con bubble CSS (hover + focus). */
export function tooltip(text: string): HTMLElement {
  const wrap = el('span', { className: 'tooltip-wrap' });
  const btn = el('button', {
    attrs: { type: 'button' },
    aria: { label: 'Maggiori informazioni' },
    className: 'inline-flex! items-center! justify-center! w-4! h-4! rounded-full! bg-slate-200! text-slate-600! hover:bg-slate-300! text-[10px]! font-bold! py-1.5! px-2!',
    text: 'i',
  });
  const bubble = el('span', { className: 'tooltip-bubble', text });
  wrap.appendChild(btn);
  wrap.appendChild(bubble);
  return wrap;
}

/** Card stat con etichetta + valore. */
export function statCard(label: string, value: string, opts: { hint?: string; tone?: 'default' | 'positive' | 'negative' } = {}): HTMLElement {
  const tone = opts.tone ?? 'default';
  const valueColor =
    tone === 'positive' ? 'text-emerald-600' : tone === 'negative' ? 'text-rose-600' : 'text-slate-900';
  return el('div', { className: 'card flex flex-col gap-1' }, [
    el('span', { className: 'stat-label', text: label }),
    el('span', { className: `stat-value ${valueColor}`, text: value }),
    opts.hint
      ? el('span', { className: 'text-xs text-slate-500', ...renderSymbol(opts.hint) })
      : null,
  ]);
}

export function renderSymbol(sym: string, prefix: string = '', suffix: string = ''): Pick<ElOpts, 'text' | 'html'> {
  sym = sym.trim();
  if (sym.indexOf('_') < 0) {
    return { text: `${prefix}${sym}${suffix}` };
  }

  const parts = sym.split(' ');
  const mutedParts: string[] = [];
  for (const part of parts) {
    if (part.indexOf('_') > 0) {
      const [base, sub] = part.split('_');
      mutedParts.push(`${prefix}${base}<sub>${sub}</sub>${suffix}`);
      continue;
    }
    mutedParts.push(part);
  }

  return { html: mutedParts.join(' ') };
}
